import { useEffect, useMemo, useState } from 'react';
import instrument from '../instrument/u-cognitive-instrument.v0.2.json';
import officialLogo from './assets/3Fold_Labs_Logo.png';
import { brainStore, readConsent } from './brainStore.js';
import { constructSpecs, ensureAgentSetup, portableAgentPackage } from './cognitiveEngine.js';

const STORAGE_KEY = 'uos.cognitive-wizard.v0.2';
const PACKAGE_KEY = 'uos.cognitive.package.v0.2';
const actionNames = { mirror: 'MATCH', complement: 'EXPAND', counterbalance: 'CHECK' };

function readLocal(key) {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
}

function displayName(user) {
  return user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || 'Your profile';
}

function PortalHeader({ routePath, user, navigate }) {
  return <header className="portal-header">
    <button className="portal-brand" type="button" onClick={() => navigate('/dashboard')}>
      <img src={officialLogo} alt="3Fold Labs" />
      <span><b>3Fold Labs</b><small>PERSONAL AI</small></span>
    </button>
    <nav aria-label="Account navigation">
      <button className={routePath === '/dashboard' ? 'active' : ''} type="button" onClick={() => navigate('/dashboard')}>DASHBOARD</button>
      <button className={routePath === '/report' ? 'active' : ''} type="button" onClick={() => navigate('/report')}>FULL REPORT</button>
      <button className={routePath === '/profile' ? 'active' : ''} type="button" onClick={() => navigate('/profile')}>PROFILE</button>
    </nav>
    <button className="portal-user" type="button" onClick={() => navigate('/profile')}><span>{displayName(user).slice(0, 1).toUpperCase()}</span>{displayName(user)}</button>
  </header>;
}

function EmptyPortal({ navigate }) {
  return <section className="portal-empty">
    <span>NO SAVED RESULT YET</span>
    <h1>Build the setup<br /><em>that follows you.</em></h1>
    <p>Complete the 25-question workstyle to create your first saved AI profile.</p>
    <button type="button" onClick={() => { window.location.assign('/'); }}>TAKE THE ASSESSMENT →</button>
  </section>;
}

function Dashboard({ pkg, user, navigate }) {
  if (!pkg) return <EmptyPortal navigate={navigate} />;
  const answered = pkg.rawEvidence?.filter(item => item.selected?.length || item.ownWords)?.length || 0;
  const constructs = pkg.cognitiveModel?.constructs || [];
  const directives = pkg.agentSetup?.directives || [];
  const strongest = [...constructs].sort((a, b) => b.estimate.confidence - a.estimate.confidence)[0];
  const strongestSpec = constructSpecs.find(item => item.id === strongest?.constructId);
  return <section className="portal-page dashboard-page">
    <div className="portal-eyebrow"><span>YOUR 3FOLD WORKSPACE</span><span>LAST UPDATED {new Date(pkg.exportedAt || pkg.cognitiveModel?.generatedAt).toLocaleDateString()}</span></div>
    <div className="dashboard-hero">
      <div><p>WELCOME BACK, {displayName(user).toUpperCase()}</p><h1>Your AI already<br /><em>knows where to start.</em></h1></div>
      <button type="button" onClick={() => navigate('/report')}>OPEN MY FULL REPORT ↗</button>
    </div>
    <div className="portal-stats">
      <article><span>ANSWERS CAPTURED</span><strong>{answered}<small>/ 25</small></strong></article>
      <article><span>WORKSTYLE SIGNALS</span><strong>{constructs.length}</strong></article>
      <article><span>AI BEHAVIORS</span><strong>{directives.length}</strong></article>
    </div>
    <div className="dashboard-grid">
      <article className="dashboard-primary">
        <span>STRONGEST CURRENT SIGNAL</span>
        <h2>{strongestSpec?.label || 'Your workstyle map'}</h2>
        <p>{strongestSpec?.definition || 'Your complete assessment is saved and ready.'}</p>
        {strongest && <div className="portal-axis"><small>{strongest.estimate.lowAnchor}</small><i><b style={{ left: `${(strongest.estimate.score + 1) / 2 * 100}%` }} /></i><small>{strongest.estimate.highAnchor}</small></div>}
      </article>
      <aside className="dashboard-actions">
        <span>YOUR NEXT MOVE</span>
        <button type="button" onClick={() => navigate('/report')}><b>01</b><span>Read the full report<small>Every signal, behavior, and answer.</small></span>↗</button>
        <button type="button" onClick={() => window.location.assign('/?retake=1')}><b>02</b><span>Retake the assessment<small>Build a fresh version of your setup.</small></span>↗</button>
      </aside>
    </div>
  </section>;
}

function FullReport({ pkg }) {
  const [copyNotice, setCopyNotice] = useState('');
  if (!pkg) return null;
  const constructs = pkg.cognitiveModel?.constructs || [];
  const directives = pkg.agentSetup?.directives || [];
  const evidence = pkg.rawEvidence || [];
  const setup = portableAgentPackage(pkg);
  const downloadJson = (value, filename) => {
    const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  };
  const copyForAi = async () => {
    await navigator.clipboard.writeText(pkg.agentSetup.installText);
    setCopyNotice('COPIED — PASTE THIS INTO YOUR AI');
    setTimeout(() => setCopyNotice(''), 2600);
  };
  return <section className="portal-page report-page">
    <div className="portal-eyebrow"><span>YOUR COMPLETE WORKSTYLE</span><button type="button" onClick={() => downloadJson(pkg, '3fold-cognitive-raw-data.json')}>DOWNLOAD RAW DATA ↓</button></div>
    <header className="portal-report-head"><p>YOUR</p><h1>COGNITIVE<br /><em>INTERFACE MAP.</em></h1><span>{constructs.length} signals built from your 25-question assessment.</span></header>
    <section className="portal-agent-setup">
      <div>
        <span>YOUR PORTABLE AI SETUP</span>
        <h2>This one tells<br />your AI what to do.</h2>
        <p>{pkg.agentSetup.directives.length} direct, non-repeating behaviors are ready to use. Your strongest written preferences are translated into actions, while the scoring evidence stays out of the way.</p>
      </div>
      <div className="portal-agent-actions">
        <button type="button" onClick={copyForAi}>COPY FOR MY AI →</button>
        <button type="button" onClick={() => downloadJson(setup, '3fold-ai-workstyle.json')}>DOWNLOAD AI SETUP ↓</button>
        <small>{copyNotice || 'Paste the copied instructions into ChatGPT, Grok, Claude, Gemini, or another AI.'}</small>
      </div>
    </section>
    <div className="portal-constructs">
      {constructs.map((construct, index) => {
        const spec = constructSpecs.find(item => item.id === construct.constructId);
        return <article key={construct.constructId}>
          <div><span>SIGNAL / {String(index + 1).padStart(2, '0')}</span><b>{Math.round(construct.estimate.confidence * 100)}% CONFIDENCE</b></div>
          <h2>{spec?.label || construct.constructId}</h2>
          <p>{spec?.definition || construct.operationalDefinition}</p>
          <div className="portal-axis"><small>{construct.estimate.lowAnchor}</small><i><b style={{ left: `${(construct.estimate.score + 1) / 2 * 100}%` }} /></i><small>{construct.estimate.highAnchor}</small></div>
        </article>;
      })}
    </div>
    <section className="portal-rules">
      <header><span>YOUR AI COLLABORATION PLAN</span><h2>How AI should<br />work with you.</h2></header>
      <div>{directives.map((directive, index) => <article key={directive.id}>
        <b>{String(index + 1).padStart(2, '0')}</b>
        <strong>{directive.source === 'user_authored_response' ? 'DIRECT' : (actionNames[directive.mode] || 'DEFAULT')}</strong>
        <p>{directive.instruction}</p>
        <span>ACTIVE</span>
      </article>)}</div>
    </section>
    <section className="portal-evidence">
      <header><span>YOUR COMPLETE RESPONSE RECORD</span><h2>Every answer.<br />Nothing missing.</h2><p>This is the exact evidence used to build your workstyle map.</p></header>
      <div>{instrument.questions.map(question => {
        const response = evidence.find(item => item.questionId === question.id) || {};
        const selected = response.selected || [];
        return <article key={question.id}>
          <div><b>{String(question.number).padStart(2, '0')}</b><span>{question.domain.toUpperCase()}</span></div>
          <section><h3>{question.prompt}</h3>
            {selected.filter(id => id !== 'E').length > 0
              ? <ul>{selected.filter(id => id !== 'E').map(id => {
                const option = question.options.find(item => item.id === id);
                return <li key={id}><b>{id}</b><span>{option?.text || id}</span></li>;
              })}</ul>
              : <p className="portal-no-answer">No structured answer selected.</p>}
            {response.ownWords && <blockquote><span>IN YOUR OWN WORDS</span>{response.ownWords}</blockquote>}
          </section>
        </article>;
      })}</div>
    </section>
  </section>;
}

function Profile({ user, onUserChange }) {
  const [name, setName] = useState(displayName(user));
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  async function save(event) {
    event.preventDefault();
    setBusy(true); setNotice('');
    try {
      const updated = await brainStore.updateProfile({ fullName: name });
      onUserChange(updated);
      setNotice('Profile updated.');
    } catch (error) { setNotice(error.message || 'Profile update needs attention.'); }
    finally { setBusy(false); }
  }
  async function signOut() {
    setBusy(true);
    await brainStore.signOut();
    window.location.replace('/account');
  }
  return <section className="portal-page profile-page">
    <div className="portal-eyebrow"><span>ACCOUNT / PROFILE</span><span>3FOLD LABS</span></div>
    <div className="profile-layout">
      <header><p>YOUR PROFILE</p><h1>One account.<br /><em>Your AI setup.</em></h1><span>Manage the identity attached to your saved workstyle and results.</span></header>
      <form onSubmit={save}>
        <label><span>FULL NAME</span><input value={name} onChange={event => setName(event.target.value)} autoComplete="name" /></label>
        <label><span>EMAIL</span><input value={user?.email || ''} disabled /></label>
        <label><span>ACCOUNT ID</span><input value={user?.id || ''} disabled /></label>
        <button type="submit" disabled={busy || !name.trim()}>{busy ? 'SAVING…' : 'SAVE PROFILE →'}</button>
        <button className="profile-signout" type="button" disabled={busy} onClick={signOut}>SIGN OUT</button>
        {notice && <p>{notice}</p>}
      </form>
    </div>
  </section>;
}

export default function MemberPortal({ routePath, navigate }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [error, setError] = useState('');
  const localPackage = useMemo(() => readConsent() ? readLocal(PACKAGE_KEY) : null, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { session } = await brainStore.authSession();
        if (!session?.user || session.user.is_anonymous) {
          window.location.replace('/account');
          return;
        }
        const params = new URLSearchParams(window.location.search);
        if (params.get('claim') === '1' && localPackage && readConsent()) {
          const state = readLocal(STORAGE_KEY) || { answers: {}, reviews: {}, ruleOverrides: {} };
          await brainStore.claimLocalResult(instrument, state, localPackage);
          window.history.replaceState({}, '', routePath);
        }
        const stored = await brainStore.latestModel();
        if (!alive) return;
        setUser(session.user);
        setPkg(ensureAgentSetup(instrument, stored || localPackage));
      } catch (cause) {
        if (alive) setError(cause.message || 'Your account could not be loaded.');
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="portal-loading"><span>3FOLD LABS</span><b>Opening your workspace…</b></div>;
  if (error) return <div className="portal-loading error"><span>ACCOUNT NEEDS ATTENTION</span><b>{error}</b><button type="button" onClick={() => window.location.assign('/account')}>RETURN TO SIGN IN</button></div>;
  return <div className="member-portal">
    <PortalHeader routePath={routePath} user={user} navigate={navigate} />
    <main>
      {routePath === '/dashboard' && <Dashboard pkg={pkg} user={user} navigate={navigate} />}
      {routePath === '/report' && (pkg ? <FullReport pkg={pkg} /> : <EmptyPortal navigate={navigate} />)}
      {routePath === '/profile' && <Profile user={user} onUserChange={setUser} />}
    </main>
  </div>;
}
